// Lib
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

// Components
import FloatingPanel from '../../../../../../components/tooltips/FloatingPanel';
import PopupPanelMouseManager from '../../../../../../components/popupPanel/PopupPanelMouseManager';
import TagEditContent from '../edit/TagEditContent';
import TagEditMovePopup from './TagEditMovePopup';
import TagColorPopup from './TagColorPopup';

import { TagEditContentProps } from '../tagComponentPropTypes';
import { getTagUsageData } from '../../../../../../element/tags/tagsService';
import { fetchElements } from '../../../../../../element/elementService';
import { getElementIdsForUpdate, getHighestTagBoardId, partitionTagRootsByEditability } from '../tagUtils';
import { updateTag } from '../../../../../../../common/tags/tagActions';
import { createNewTagThunk, replaceTagIdForElementsThunk } from '../tagThunks';
import useThunkDispatch from '../../../../../../store/useThunkDispatch';

// Constants
import { TooltipAlignment, TooltipPositions } from '../../../../../../components/tooltips/tooltipConstants';
import { PopupIds } from '../../../../../../components/popupPanel/popupConstants';

// Types
import { Tag } from '../../../../../../../common/tags/tagTypes';
import {
    BoardVirtualPathMap,
    TaggedElementsData,
} from '../../../../../../../common/api/tags/tagElementsRetrievalRouteTypes';

type Props = TagEditContentProps & {
    showEditingPopup: boolean;
};

const TagEditPopup = (props: Props) => {
    const { tag, showEditingPopup, closePopups, userRootBoardId, userRootPath, currentUserId } = props;

    const dispatch = useDispatch();
    const thunkDispatch = useThunkDispatch();

    const [taggedElements, setTaggedElements] = useState<TaggedElementsData[]>([]);
    const [boardVirtualPathToRootMap, setBoardVirtualPathToRootMap] = useState<BoardVirtualPathMap | null>(null);

    useEffect(() => {
        if (!tag) return;

        const fetchUsageData = async () => {
            const { taggedElements, boardVirtualPathToRootMap } = await getTagUsageData(tag.id, userRootBoardId);
            setTaggedElements(taggedElements);
            setBoardVirtualPathToRootMap(boardVirtualPathToRootMap);

            dispatch(fetchElements({ elementIds: taggedElements.map((el) => el.elementId) }));
        };

        fetchUsageData();
    }, [tag]);

    if (!tag) return null;

    const updateTagHandler = (changes: Partial<Tag>) => {
        if (!tag || !userRootPath || !boardVirtualPathToRootMap) return;

        const { tagRootsToUpdate, tagRootsNotToUpdate } = partitionTagRootsByEditability(
            tag,
            userRootPath,
            boardVirtualPathToRootMap,
        );

        // If we want to update all tag roots with the changes, we can directly update the tag
        if (tagRootsNotToUpdate.length === 0) {
            dispatch(updateTag(tag.id, changes));
            return;
        }

        // Otherwise remove those tag roots from the old tag
        dispatch(updateTag(tag.id, { rootBoardIds: tagRootsNotToUpdate }));

        // Create a new tag with the updated details
        const newTag = thunkDispatch(
            createNewTagThunk({
                ...tag,
                ...changes,
                rootBoardIds: tagRootsToUpdate,
                creator: currentUserId,
            }),
        );

        // And swap all tagged elements with the old tag to the new tag for those roots
        const highestRootId = getHighestTagBoardId(tag, userRootPath);
        const elementIdsForUpdate = getElementIdsForUpdate(taggedElements, highestRootId, boardVirtualPathToRootMap);
        dispatch(replaceTagIdForElementsThunk(elementIdsForUpdate, tag.id, newTag.id));
    };

    return (
        <FloatingPanel
            className="TagEditPopup light !w-auto !p-0 !overflow-hidden"
            selector={`#tag-${tag.id}`}
            pollPosition
            offset={20}
            showArrow={false}
            distance={20}
            alignment={TooltipAlignment.START}
            position={TooltipPositions.RIGHT}
            show={showEditingPopup}
        >
            <PopupPanelMouseManager
                visible={showEditingPopup}
                popupId={PopupIds.TAG_EDIT_MENU}
                closePopup={closePopups}
            >
                <div className="-mx-[2.4rem]">
                    <TagEditContent
                        {...props}
                        taggedElements={taggedElements}
                        boardVirtualPathToRootMap={boardVirtualPathToRootMap}
                        updateTag={updateTagHandler}
                    />
                </div>
                <TagEditMovePopup
                    {...props}
                    taggedElements={taggedElements}
                    boardVirtualPathToRootMap={boardVirtualPathToRootMap}
                    updateTag={updateTagHandler}
                />
                <TagColorPopup {...props} updateTag={updateTagHandler} />
            </PopupPanelMouseManager>
        </FloatingPanel>
    );
};

export default TagEditPopup;
